<template>
  <div class="live-transcript-overlay" v-show="isVisible">
    <div
      class="live-transcript-container ultra-modern"
      :style="{
        background: backgroundColorLocal,
        width: isMobile ? 'auto' : containerWidth,
        height: containerHeight
      }"
      v-if="displayText"
    >
      <!-- Background overlay with gradient -->
      <div class="background-overlay"></div>

      <!-- Animated background elements -->
      <div class="animated-bg-elements">
        <div v-for="n in 3" :key="`bg-element-${n}`" class="bg-element"></div>
      </div>

      <!-- Speaker section with enhanced visuals -->
      <div class="speaker-section">
        <div class="speaker-name-container" v-if="showSpeaker">
          <div class="speaker-badge">
            <div class="speaker-status" :class="{ 'active': !isPaused }"></div>
            <span>{{ currentSpeaker }}</span>
          </div>
        </div>

        <div class="transcript-speaker-avatar" :class="{ 'speaking': !isPaused }">
          <div class="avatar-glow" :class="{ 'active': !isPaused }"></div>
          <img
            v-if="speakerImage"
            :src="speakerImage"
            :alt="currentSpeaker"
            class="avatar-image"
            @error="handleImageError"
          />
          <div v-else class="avatar-fallback">
            {{ getSpeakerInitials() }}
          </div>

          <!-- Enhanced audio wave visualization -->
          <div class="avatar-wave-indicator" :class="{ 'active': !isPaused }">
            <div v-for="n in 5" :key="`wave-${n}`" class="wave-bar"></div>
          </div>
        </div>
      </div>

      <!-- Content section with modern text display -->
      <div class="transcript-content">
        <div class="transcript-text-container">
          <div
            class="transcript-text"
            :style="computedFontStyles"
            ref="transcriptTextElement"
          >
            <span v-html="typewriterText"></span>
            <span class="cursor" :class="{ 'blink': cursorBlinking }" :style="computedCursorStyles">|</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LiveTranscript',
  props: {
    // Current segment text being spoken
    text: {
      type: String,
      default: ''
    },
    // Speaker name
    speaker: {
      type: String,
      default: ''
    },
    // Speaker image URL
    speakerImage: {
      type: String,
      default: ''
    },
    // Whether to show the transcript
    visible: {
      type: Boolean,
      default: true
    },
    // Whether to show the speaker name
    showSpeaker: {
      type: Boolean,
      default: true
    },
    // Maximum length before truncating text
    maxLength: {
      type: Number,
      default: 400
    },
    messageId: {
      required: true,
    },
    // Base typing speed in milliseconds per character
    typingSpeed: {
      type: Number,
      default: 48
    },
    // Whether to restart typing animation when text changes
    restartOnTextChange: {
      type: Boolean,
      default: true
    },
    // Voice playback speed (1.0 = normal)
    voiceSpeed: {
      type: Number,
      default: 1.0
    },
    // Background color for the transcript container
    backgroundColor: {
      type: String,
      default: 'rgba(51, 33, 186, 0.6)'
    },
    // Font configuration props
    fontFamily: {
      type: String,
      default: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    fontSize: {
      type: [String, Number],
      default: 18
    },
    fontWeight: {
      type: [String, Number],
      default: 400
    },
    lineHeight: {
      type: [String, Number],
      default: 1.5
    },
    letterSpacing: {
      type: [String, Number],
      default: 0.3
    },
    textColor: {
      type: String,
      default: 'white'
    },
    textShadow: {
      type: String,
      default: '0 1px 2px rgba(0, 0, 0, 0.3)'
    }
  },
  data() {
    return {
      backgroundColorLocal: localStorage.getItem(`transcript_background_${this.messageId}`) || this.backgroundColor,
      isVisible: this.visible,
      currentSpeaker: this.speaker,
      fullText: this.text,
      displayText: '',
      typewriterText: '',
      typingIndex: 0,
      typingTimer: null,
      cursorBlinking: false,
      cursorTimer: null,
      isPaused: false,
      pauseTimer: null,
      // Container dimensions - calculated before animation starts
      containerWidth: 'auto',
      containerHeight: 'auto',
      // Text dimensions
      textWidth: 0,
      textHeight: 0,
      // Base container sizes
      minContainerWidth: 300,
      maxContainerWidthDesktop: '70%',
      maxContainerWidthMobile: '90%',
      maxWidthInPixels: 800,
      minContainerHeight: 120,
      // Character width estimation for pre-sizing (pixels per character)
      charWidthEstimate: 12,
      // Line height estimation (pixels)
      lineHeightEstimate: 25,
      // Padding and other space considerations
      horizontalPadding: 36,
      verticalPadding: 24,
      avatarWidth: 100,
      avatarSpacing: 16,
      // Flag to track if we're on mobile
      isMobile: false,
      // Punctuation that indicates pauses with relative pause length multipliers
      pausePunctuation: {
        '.': 10,
        '!': 10,
        '?': 10,
        ',': 5,
        ';': 5,
        ':': 5,
        '-': 5,
        '—': 5,
        '(': 2,
        ')': 2,
        '\n': 5,
        '\r': 5
      },
      // Regular expressions for special text patterns
      regexPatterns: {
        citation: /\*\*c\d+\*\*/g,
        abbreviation: /(?:\b[A-Z]\.)+(?:[A-Z]\b|[A-Z]\.)/g
      }
    }
  },
  computed: {
    // Computed style object for font configuration
    computedFontStyles() {
      // Ensure fontSize always has 'px' unit
      let fontSize;
      if (typeof this.fontSize === 'number') {
        fontSize = `${this.fontSize}px`;
      } else if (typeof this.fontSize === 'string' && !this.fontSize.includes('px')) {
        // If it's a string number without px, add px
        const numValue = parseFloat(this.fontSize);
        fontSize = isNaN(numValue) ? this.fontSize : `${numValue}px`;
      } else {
        fontSize = this.fontSize;
      }

      // Ensure letterSpacing has units
      let letterSpacing;
      if (typeof this.letterSpacing === 'number') {
        letterSpacing = `${this.letterSpacing}px`;
      } else {
        letterSpacing = this.letterSpacing;
      }

      const lineHeight = typeof this.lineHeight === 'number' ? this.lineHeight.toString() : this.lineHeight;
      const fontWeight = typeof this.fontWeight === 'number' ? this.fontWeight.toString() : this.fontWeight;

      // Use CSS custom properties to ensure they override any existing styles
      return {
        '--font-family': this.fontFamily,
        '--font-size': fontSize,
        '--font-weight': fontWeight,
        '--line-height': lineHeight,
        '--letter-spacing': letterSpacing,
        '--text-color': this.textColor,
        '--text-shadow': this.textShadow,
        // Fallback inline styles
        fontFamily: this.fontFamily,
        fontSize: fontSize,
        fontWeight: fontWeight,
        lineHeight: lineHeight,
        letterSpacing: letterSpacing,
        color: this.textColor,
        textShadow: this.textShadow,
        minHeight: '1.5em',
        wordWrap: 'break-word',
        width: '100%'
      }
    },
    // Cursor styles to match the text
    computedCursorStyles() {
      const fontSize = typeof this.fontSize === 'number' ? `${this.fontSize}px` : this.fontSize;

      return {
        fontFamily: this.fontFamily,
        fontSize: fontSize,
        fontWeight: this.fontWeight,
        color: this.textColor
      }
    },
    // Dynamic character width estimation based on font size
    dynamicCharWidth() {
      const baseFontSize = 18;
      const currentFontSize = typeof this.fontSize === 'number' ? this.fontSize : parseInt(this.fontSize);
      return this.charWidthEstimate * (currentFontSize / baseFontSize);
    },
    // Dynamic line height estimation
    dynamicLineHeight() {
      const baseFontSize = 18;
      const currentFontSize = typeof this.fontSize === 'number' ? this.fontSize : parseInt(this.fontSize);
      const lineHeightMultiplier = typeof this.lineHeight === 'number' ? this.lineHeight : parseFloat(this.lineHeight);

      return currentFontSize * lineHeightMultiplier;
    }
  },
  watch: {
    text: {
      immediate: true,
      handler(newText) {
        this.fullText = newText;
        this.processText();
        this.calculateContainerSize();

        if (this.restartOnTextChange && this.visible) {
          this.resetTypewriter();
        }
      }
    },
    speaker(newSpeaker) {
      this.currentSpeaker = newSpeaker;
    },
    visible(newVisible) {
      const oldVisible = this.isVisible;
      this.isVisible = newVisible;

      if (!newVisible) {
        this.stopTypewriter();
        this.clearPauseTimer();
        this.cursorBlinking = false;
        this.isPaused = true;
      } else if (this.displayText) {
        this.backgroundColorLocal = localStorage.getItem(`transcript_background_${this.messageId}`) || this.backgroundColor;
        this.calculateContainerSize();

        if (!oldVisible && newVisible) {
          this.resetTypewriter();
        } else {
          setTimeout(() => {
            this.startTypewriter();
          }, 200);
        }
      }
    },
    voiceSpeed() {
      if (this.typingIndex < this.displayText.length) {
        this.resetTypewriter();
      }
    },
    backgroundColor(newColor) {
      this.backgroundColorLocal = newColor;
      localStorage.setItem(`transcript_background_${this.messageId}`, newColor);
    },
    // Watch font-related props and recalculate container size
    fontSize() {
      console.log('Font size changed to:', this.fontSize, 'Type:', typeof this.fontSize);
      this.calculateContainerSize();
      this.forceUpdateFontStyles();
    },
    fontFamily() {
      console.log('Font family changed to:', this.fontFamily);
      this.calculateContainerSize();
      this.forceUpdateFontStyles();
    },
    lineHeight() {
      console.log('Line height changed to:', this.lineHeight);
      this.calculateContainerSize();
      this.forceUpdateFontStyles();
    },
    fontWeight() {
      console.log('Font weight changed to:', this.fontWeight);
      this.forceUpdateFontStyles();
    },
    letterSpacing() {
      console.log('Letter spacing changed to:', this.letterSpacing);
      this.forceUpdateFontStyles();
    },
    textColor() {
      console.log('Text color changed to:', this.textColor);
      this.forceUpdateFontStyles();
    }
  },
  mounted() {
    console.log('LiveTranscript mounted with font settings:', {
      fontFamily: this.fontFamily,
      fontSize: this.fontSize,
      fontWeight: this.fontWeight,
      textColor: this.textColor
    });

    this.backgroundColorLocal = localStorage.getItem(`transcript_background_${this.messageId}`) || this.backgroundColor;
    this.detectMobileDevice();
    window.addEventListener('resize', this.handleResize);
    this.calculateContainerSize();
    this.startCursorBlink();

    // Force initial font styles
    this.forceUpdateFontStyles();
  },
  beforeDestroy() {
    this.stopTypewriter();
    this.stopCursorBlink();
    this.clearPauseTimer();
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    detectMobileDevice() {
      this.isMobile = window.innerWidth <= 768;
    },

    handleResize() {
      const wasMobile = this.isMobile;
      this.detectMobileDevice();

      if (wasMobile !== this.isMobile) {
        this.calculateContainerSize();
      }
    },

    calculateContainerSize() {
      if (!this.displayText) {
        this.containerWidth = 'auto';
        this.containerHeight = 'auto';
        return;
      }

      const avatarSize = this.isMobile ? 60 : this.avatarWidth;
      const avatarGap = this.isMobile ? 12 : this.avatarSpacing;

      // Use dynamic character width and line height based on current font settings
      const charWidth = this.isMobile ? this.dynamicCharWidth * 0.7 : this.dynamicCharWidth;
      const lineHeight = this.isMobile ? this.dynamicLineHeight * 0.9 : this.dynamicLineHeight;

      const maxWidthPercent = this.isMobile ? 0.92 : 0.75;
      const maxWidthPixels = this.maxWidthInPixels;

      let contentWidth = Math.min(
        Math.max(this.displayText.length * charWidth, this.minContainerWidth),
        window.innerWidth * maxWidthPercent,
        maxWidthPixels
      );

      const availableTextWidth = contentWidth - avatarSize - avatarGap - this.horizontalPadding;
      const charsPerLine = Math.max(10, Math.floor(availableTextWidth / charWidth));
      const estimatedLines = Math.max(3, Math.ceil(this.displayText.length / charsPerLine));

      const textHeight = estimatedLines * lineHeight;
      const minContentHeight = Math.max(this.minContainerHeight, avatarSize + 20);
      const contentHeight = Math.max(textHeight + this.verticalPadding, minContentHeight);

      const speakerNameHeight = this.showSpeaker ? (this.isMobile ? 20 : 28) : 0;

      this.containerWidth = `${contentWidth}px`;
      this.containerHeight = `${Math.min(contentHeight + speakerNameHeight, window.innerHeight * 0.6)}px`;
    },

    handleImageError(event) {
      event.target.style.display = 'none';
      const container = event.target.parentNode;

      const fallback = document.createElement('div');
      fallback.className = 'avatar-fallback';
      fallback.textContent = this.getSpeakerInitials();
      container.appendChild(fallback);
    },

    getSpeakerInitials() {
      if (!this.currentSpeaker) return '?';
      return this.currentSpeaker
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
    },

    processText() {
      if (!this.fullText) {
        this.displayText = '';
        return;
      }

      let cleanText = this.fullText.replace(/<[^>]*>/g, '');
      cleanText = cleanText.replace(/\[\d+\]/g, '');
      cleanText = cleanText.replace(/\s+/g, ' ').trim();

      if (cleanText.length > this.maxLength) {
        cleanText = cleanText.substring(0, this.maxLength) + '...';
      }

      this.displayText = cleanText;
    },

    startTypewriter() {
      this.stopTypewriter();
      this.clearPauseTimer();
      this.isPaused = false;

      if (this.typingIndex >= this.displayText.length) {
        this.cursorBlinking = true;
        return;
      }

      this.typeNextCharacter();
    },

    typeNextCharacter() {
      if (this.typingIndex >= this.displayText.length) {
          this.cursorBlinking = true;
          this.isPaused = true;
          return;
      }

      this.typewriterText += this.displayText.charAt(this.typingIndex);

      let shouldPause = false;
      let pauseMultiplier = 0;

      const currentChar = this.displayText.charAt(this.typingIndex);
      const nextChar = this.typingIndex + 1 < this.displayText.length ? this.displayText.charAt(this.typingIndex + 1) : '';
      const prevChar = this.typingIndex > 0 ? this.displayText.charAt(this.typingIndex - 1) : '';

      const lookAhead = this.typingIndex + 10 < this.displayText.length ?
          this.displayText.substring(this.typingIndex + 1, this.typingIndex + 10) :
          this.displayText.substring(this.typingIndex + 1);

      const isStartingCitation = currentChar === 'c' && /^\d/.test(nextChar);
      const isEndingCitation = /\d/.test(currentChar) && prevChar && /c\d+c/.test(this.displayText.substring(this.typingIndex - 4, this.typingIndex + 1)) && !/\d/.test(nextChar);

      if (this.pausePunctuation[currentChar]) {
          const contextAfter = this.displayText.substring(this.typingIndex + 1, this.typingIndex + 15);
          const isAbbreviation = /^[A-Z]\.([A-Z]|[a-z])/.test(prevChar + currentChar + nextChar);
          const followedByCitation = /^\s*c\d+/.test(contextAfter);

          if (currentChar === '.' && (isAbbreviation || nextChar === ' ' && /[A-Z]/.test(contextAfter.trim().charAt(0)))) {
          shouldPause = true;
          pauseMultiplier = this.pausePunctuation[currentChar];
          } else if (followedByCitation) {
          shouldPause = true;
          pauseMultiplier = 10;
          } else {
          shouldPause = true;
          pauseMultiplier = this.pausePunctuation[currentChar];
          }
      }

      if (isStartingCitation) {
          shouldPause = true;
          pauseMultiplier = 10;
      } else if (isEndingCitation) {
          shouldPause = true;
          pauseMultiplier = 10;
      }

      this.typingIndex++;
      this.cursorBlinking = false;

      if (shouldPause && pauseMultiplier > 0) {
          this.addNaturalPause(pauseMultiplier);
      } else {
          this.scheduleNextCharacter();
      }
    },

    addNaturalPause(multiplier) {
      this.isPaused = true;

      const basePause = this.typingSpeed * multiplier;
      const adjustedPause = basePause / this.voiceSpeed;

      this.pauseTimer = setTimeout(() => {
        this.isPaused = false;
        this.scheduleNextCharacter();
      }, adjustedPause);
    },

    scheduleNextCharacter() {
      const delay = this.getTypingDelay();

      this.typingTimer = setTimeout(() => {
        this.typeNextCharacter();
      }, delay);
    },

    getTypingDelay() {
      let delay = this.typingSpeed / this.voiceSpeed;
      const randomFactor = 0.9 + (Math.random() * 0.2);
      delay *= randomFactor;

      return Math.max(10, Math.floor(delay));
    },

    stopTypewriter() {
      if (this.typingTimer) {
        clearTimeout(this.typingTimer);
        this.typingTimer = null;
      }
    },

    clearPauseTimer() {
      if (this.pauseTimer) {
        clearTimeout(this.pauseTimer);
        this.pauseTimer = null;
      }
    },

    resetTypewriter() {
      this.stopTypewriter();
      this.clearPauseTimer();
      this.typingIndex = 0;
      this.typewriterText = '';
      this.isPaused = false;

      this.calculateContainerSize();
      this.startTypewriter();
    },

    startCursorBlink() {
      this.cursorTimer = setInterval(() => {
        if (this.typingIndex >= this.displayText.length) {
          this.cursorBlinking = !this.cursorBlinking;
        }
      }, 500);
    },

    stopCursorBlink() {
      if (this.cursorTimer) {
        clearInterval(this.cursorTimer);
        this.cursorTimer = null;
      }
    },

    // Force update font styles directly on DOM element
    forceUpdateFontStyles() {
      this.$nextTick(() => {
        const textElement = this.$refs.transcriptTextElement;
        if (textElement) {
          // Ensure fontSize always has 'px' unit
          let fontSize;
          if (typeof this.fontSize === 'number') {
            fontSize = `${this.fontSize}px`;
          } else if (typeof this.fontSize === 'string' && !this.fontSize.includes('px')) {
            const numValue = parseFloat(this.fontSize);
            fontSize = isNaN(numValue) ? this.fontSize : `${numValue}px`;
          } else {
            fontSize = this.fontSize;
          }

          // Ensure letterSpacing has units
          let letterSpacing;
          if (typeof this.letterSpacing === 'number') {
            letterSpacing = `${this.letterSpacing}px`;
          } else {
            letterSpacing = this.letterSpacing;
          }

          const lineHeight = typeof this.lineHeight === 'number' ? this.lineHeight.toString() : this.lineHeight;
          const fontWeight = typeof this.fontWeight === 'number' ? this.fontWeight.toString() : this.fontWeight;

          console.log('Force updating DOM with processed values:', {
            fontSize,
            fontFamily: this.fontFamily,
            fontWeight,
            lineHeight,
            letterSpacing,
            color: this.textColor
          });

          // Apply styles directly to DOM element with setProperty for CSS variables
          textElement.style.setProperty('--font-size', fontSize);
          textElement.style.setProperty('--font-family', this.fontFamily);
          textElement.style.setProperty('--font-weight', fontWeight);
          textElement.style.setProperty('--line-height', lineHeight);
          textElement.style.setProperty('--letter-spacing', letterSpacing);
          textElement.style.setProperty('--text-color', this.textColor);
          textElement.style.setProperty('--text-shadow', this.textShadow);

          // Also set regular styles as fallback
          textElement.style.fontFamily = this.fontFamily;
          textElement.style.fontSize = fontSize;
          textElement.style.fontWeight = fontWeight;
          textElement.style.lineHeight = lineHeight;
          textElement.style.letterSpacing = letterSpacing;
          textElement.style.color = this.textColor;
          textElement.style.textShadow = this.textShadow;

          // Force a reflow
          textElement.offsetHeight;
        }
      });
    }
  }
}
</script>

<style>
/* Remove 'scoped' to prevent Vue from adding data-v-* attributes that increase specificity */
/* Live Transcript Component Styles */
.live-transcript-overlay {
  position: absolute;
  bottom: 80px;
  left: 0;
  right: 0;
  z-index: 10;
  display: flex;
  justify-content: center;
  pointer-events: none;
}

.live-transcript-container {
  position: relative;
  border-radius: 16px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
  backdrop-filter: blur(12px);
  overflow: hidden;
  min-height: 120px;
}

.live-transcript-overlay .ultra-modern {
  border: 1px solid rgba(255, 255, 255, 0.1);
  animation: live-transcript-modernAppear 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes live-transcript-modernAppear {
  0% { transform: translateY(20px); opacity: 0; }
  100% { transform: translateY(0); opacity: 1; }
}

.live-transcript-overlay .animated-bg-elements {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: -1;
}

.bg-element {
  position: absolute;
  background: linear-gradient(135deg, rgba(80, 100, 255, 0.1), rgba(120, 200, 255, 0.05));
  border-radius: 50%;
  animation: moveElement 20s infinite alternate;
  opacity: 0.4;
}

.bg-element:nth-child(1) {
  width: 200px;
  height: 200px;
  top: -100px;
  right: -50px;
  animation-delay: 0s;
}

.bg-element:nth-child(2) {
  width: 150px;
  height: 150px;
  bottom: -50px;
  left: 20%;
  animation-delay: -5s;
}

.bg-element:nth-child(3) {
  width: 100px;
  height: 100px;
  top: 30%;
  left: -30px;
  animation-delay: -10s;
}

@keyframes moveElement {
  0% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(20px, 10px) scale(1.1); }
  100% { transform: translate(-10px, 15px) scale(0.95); }
}

.speaker-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.speaker-name-container {
  margin-bottom: 6px;
  width: 100%;
}

.speaker-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 14px;
  font-weight: 500;
  color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.speaker-status {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #888;
  position: relative;
}

.speaker-status.active {
  background-color: #4CAF50;
  box-shadow: 0 0 8px rgba(76, 175, 80, 0.6);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7); }
  70% { box-shadow: 0 0 0 6px rgba(76, 175, 80, 0); }
  100% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0); }
}

.transcript-speaker-avatar {
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
}

.transcript-speaker-avatar.speaking {
  transform: scale(1.05);
  border-color: rgba(255, 255, 255, 0.5);
}

.avatar-glow {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  z-index: -1;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.avatar-glow.active {
  opacity: 1;
  box-shadow: 0 0 20px 5px rgba(100, 200, 255, 0.4);
  animation: glowPulse 2s infinite;
}

@keyframes glowPulse {
  0% { box-shadow: 0 0 15px 5px rgba(100, 200, 255, 0.4); }
  50% { box-shadow: 0 0 25px 8px rgba(100, 200, 255, 0.6); }
  100% { box-shadow: 0 0 15px 5px rgba(100, 200, 255, 0.4); }
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #3a4a6b, #2c3e50);
  color: white;
  font-weight: bold;
  font-size: 28px;
  letter-spacing: 1px;
}

.avatar-wave-indicator {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 24px;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: flex-end;
  gap: 3px;
  padding: 4px;
  transition: background-color 0.3s ease;
}

.avatar-wave-indicator.active {
  background: rgba(25, 118, 210, 0.7);
  background: linear-gradient(to right, rgba(25, 118, 210, 0.7), rgba(66, 165, 245, 0.7));
}

.wave-bar {
  width: 3px;
  height: 3px;
  border-radius: 1.5px;
  background: white;
  transition: height 0.2s ease;
}

.active .wave-bar {
  animation: enhancedWaveAnimation 0.8s ease-in-out infinite;
}

.active .wave-bar:nth-child(1) { animation-delay: -0.6s; }
.active .wave-bar:nth-child(2) { animation-delay: -0.4s; }
.active .wave-bar:nth-child(3) { animation-delay: -0.2s; }
.active .wave-bar:nth-child(4) { animation-delay: 0s; }
.active .wave-bar:nth-child(5) { animation-delay: -0.8s; }

@keyframes enhancedWaveAnimation {
  0% { height: 3px; }
  40% { height: 16px; }
  80% { height: 5px; }
  100% { height: 3px; }
}

.transcript-content {
  flex: 1;
  overflow: hidden;
}

.transcript-text-container {
  position: relative;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  min-height: 90px;
  display: flex;
  align-items: center;
}

.live-transcript-overlay .transcript-text {
  /* Use CSS custom properties with fallbacks and !important to override any conflicting styles */
  font-family: var(--font-family, 'system-ui') !important;
  font-size: var(--font-size, 18px) !important;
  font-weight: var(--font-weight, 400) !important;
  line-height: var(--line-height, 1.5) !important;
  letter-spacing: var(--letter-spacing, 0.3px) !important;
  color: var(--text-color, white) !important;
  text-shadow: var(--text-shadow, '0 1px 2px rgba(0, 0, 0, 0.3)') !important;

  /* Layout properties */
  position: relative;
  z-index: 1;
  min-height: 1.5em;
  word-wrap: break-word;
  width: 100%;
}

.live-transcript-overlay .cursor {
  display: inline-block;
  font-weight: bold;
  margin-left: 1px;
  position: relative;
  top: -1px;
}

.live-transcript-overlay .cursor.blink {
  animation: live-transcript-blink 1s step-end infinite;
}

@keyframes live-transcript-blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

@media (max-width: 768px) {
  .live-transcript-overlay {
    bottom: 16px;
  }

  .live-transcript-container {
    max-width: 92%;
    padding: 12px;
    border-radius: 12px;
  }

  .transcript-speaker-avatar {
    width: 60px;
    height: 60px;
    min-width: 60px;
  }

  .avatar-fallback {
    font-size: 18px;
  }

  .avatar-wave-indicator {
    height: 20px;
    padding: 3px;
  }

  .wave-bar {
    width: 2px;
  }

  @keyframes enhancedWaveAnimation {
    0% { height: 2px; }
    40% { height: 12px; }
    80% { height: 4px; }
    100% { height: 2px; }
  }
}

.background-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.05), rgba(0, 0, 0, 0.05));
  pointer-events: none;
  z-index: 0;
}

</style>
